// This file is part of MinIO Operator
// Copyright (c) 2022 MinIO, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { createAsyncThunk } from "@reduxjs/toolkit";
import { getDefaultAffinity, getNodeSelector } from "../../utils";
import { resetPoolForm } from "./addPoolSlice";
import { getTenantAsync } from "../../tenantDetailsAsync";
import { RootState } from "../../../../../app/store";
import { ErrorResponseHandler } from "../../../../../common/types";
import { setErrorSnackBarMessage } from "../../../../systemSlice";
import { generatePoolName } from "../../../utils/utils";
import { oprRpc } from "../../../../../api";
import {
  MinioOpPool,
  OperatorRpcsAddPoolBody,
} from "../../../../../api/operatorApi";
import { AsyncResourceRequest } from "../../../../../common/common.types";

export const addPoolAsync = createAsyncThunk(
  "addPool/addPoolAsync",
  async (
    req: AsyncResourceRequest,
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;

    const tenant = state.tenants.tenantInfo;
    const selectedStorageClass = state.addPool.setup.storageClass;
    const numberOfNodes = state.addPool.setup.numberOfNodes;
    const volumeSize = state.addPool.setup.volumeSize;
    const volumesPerServer = state.addPool.setup.volumesPerServer;
    const affinityType = state.addPool.affinity.podAffinity;
    const nodeSelectorLabels = state.addPool.affinity.nodeSelectorLabels;
    const withPodAntiAffinity = state.addPool.affinity.withPodAntiAffinity;
    const tolerations = state.addPool.tolerations;
    const securityContextEnabled =
      state.addPool.configuration.securityContextEnabled;
    const securityContext = state.addPool.configuration.securityContext;
    const customRuntime = state.addPool.configuration.customRuntime;
    const runtimeClassName = state.addPool.configuration.runtimeClassName;

    if (tenant === null) {
      return;
    }

    const poolName = generatePoolName(tenant.pools!);

    let affinityObject = {};

    switch (affinityType) {
      case "default":
        affinityObject = {
          affinity: getDefaultAffinity(tenant.name!, poolName),
        };
        break;
      case "nodeSelector":
        affinityObject = {
          affinity: getNodeSelector(
            nodeSelectorLabels,
            withPodAntiAffinity,
            tenant.name!,
            poolName,
          ),
        };
        break;
    }

    const tolerationValues = tolerations.filter(
      (toleration) => toleration.key?.trim() !== "",
    );

    let runtimeClass = {};

    if (customRuntime) {
      runtimeClass = {
        runtimeClassName,
      };
    }

    const data: MinioOpPool = {
      name: poolName,
      servers: numberOfNodes,
      volumesPerServer: volumesPerServer,
      volumeConfiguration: {
        size: `${volumeSize * 1073741824}`,
        storageClassName: selectedStorageClass,
      },
      tolerations: tolerationValues,
      securityContext: securityContextEnabled ? securityContext : undefined,
      ...affinityObject,
      ...runtimeClass,
    };

    const request: OperatorRpcsAddPoolBody = {
      pool: data,
    };

    const poolsURL = `/tenant/${req.siteId}/details/${
      tenant?.namespace || ""
    }/${req.resourceId}/pools`;

    return oprRpc.api
      .addPool(req.siteId, tenant.namespace!, req.resourceId!, request)
      .then(() => {
        dispatch(resetPoolForm());
        dispatch(getTenantAsync(req));
        return poolsURL;
      })
      .catch((err: ErrorResponseHandler) => {
        dispatch(setErrorSnackBarMessage(err));
      });
  },
);
